import {Injectable} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {forkJoin, Observable} from 'rxjs';
import {switchMap, take} from 'rxjs/operators';
import {SharedFacadeService} from '@shared/shared-facade.service';
import {CommunicationService} from '@services/communication.service';
import {Page} from '@model/page';
import {School} from '@model/school';
import {StudentView} from '@model/student-view';
import {EmailContent} from '@model/email-content';
import {StudentSearchData} from '@model/student-search-data';
import {getInvalidEmails, StoreState} from '@redux/reducers';
import {Store, select} from '@ngrx/store';
import {InvalidEmailsLoadedAction} from '@redux/actions/school.action';
import {InvalidEmail} from '@model/invalid-email';
import {PredefinedTextContentMap} from '@model/predefined-text-content';

@Injectable()
export class CommunicationFacadeService {

  school$: Observable<School>;
  studentSearchResult$: Observable<Page<StudentView>>;
  invalidEmails$: Observable<InvalidEmail[]>;
  studentsByClass$: Observable<StudentView[]>;
  predefinedTextContentsMap$: Observable<PredefinedTextContentMap>;

  constructor(private _sharedFacade: SharedFacadeService,
              private _communicationService: CommunicationService,
              private _snackBar: MatSnackBar,
              private _store: Store<StoreState>) {
    this.school$ = this._sharedFacade.school$;
    this.studentSearchResult$ = this._sharedFacade.studentSearchResult$;
    this.predefinedTextContentsMap$ = this._sharedFacade.predefinedTextContentsMap$;
    this.invalidEmails$ = this._store.select(getInvalidEmails);
    this.studentsByClass$ = this._store.pipe(
      select(state => state.students.studentSearchResult.content)
    );
    
  }

  sendEmailToClasses(classesId: number[], emailContent: EmailContent, files: File[], senderInCopy: boolean, recipients: string[]): void {
    this.school$.pipe(
      take(1),
      switchMap(school => {
        const observables = recipients.map(recipient => this._communicationService.sendEmailToClasses(school.id, classesId, emailContent, files, senderInCopy, recipient));
        return forkJoin(observables);
      })
    ).subscribe(nbrOfEmailsSent => this._displaySnackBarWithEmailsSentMessage(nbrOfEmailsSent.reduce((acc, curr) => acc + curr, 0)));
  }

  sendEmailToStaffMembers(emailContent: EmailContent, files: File[]): void {
    this.school$.pipe(
      take(1),
      switchMap(school => this._communicationService.sendEmailToStaffMembers(school.id, emailContent, files)
      )
    ).subscribe(nbrOfEmailsSent => this._displaySnackBarWithEmailsSentMessage(nbrOfEmailsSent));
  }

  sendEmailToPedagogicalTeam(studentId: number, emailContent: EmailContent, files: File[], senderInCopy: boolean): void {
    this.school$.pipe(
      take(1),
      switchMap(school =>
        this._communicationService.sendEmailToPedagogicalTeam(school.id, studentId, emailContent, files, senderInCopy))
    ).subscribe(nbrOfEmailsSent => this._displaySnackBarWithEmailsSentMessage(nbrOfEmailsSent));
  }

  private _displaySnackBarWithEmailsSentMessage(nbrOfEmailsSent: number) {
    const message = nbrOfEmailsSent > 1 ? `${nbrOfEmailsSent} mails envoyés` : `${nbrOfEmailsSent} mail envoyé`;
    return this._snackBar.open(message, null, {duration: 2000});
  }

  sendEmailToStudentsOfAttribution(attributionId: number, emailContent: EmailContent, files: File[], senderInCopy: boolean, recipients: string[]) {
    this.school$.pipe(
      take(1),
      switchMap(school => {
        const observables = recipients.map(recipient => this._communicationService.sendEmailToStudentsOfAttribution(school.id, attributionId, emailContent, files, senderInCopy, recipient));
        return forkJoin(observables);
      })
    ).subscribe(nbrOfEmailsSent => this._displaySnackBarWithEmailsSentMessage(nbrOfEmailsSent.reduce((acc, curr) => acc + curr, 0)));
  }

  searchStudents(searchData: StudentSearchData): void {
    this._sharedFacade.searchStudents(searchData);
  }

  sendCommunicationToParent(studentId: number, content: string) {
    this._sharedFacade.sendCommunicationToParent(studentId, content);
  }

  resetStudentSearchResult() {
    this._sharedFacade.resetStudentSearchResult();
  }

  loadInvalidEmails() {
    this.school$.pipe(
      take(1),
      switchMap(school =>
        this._communicationService.getInvalidEmails(school.id))
    ).subscribe(invalidEmails => this._store.dispatch(new InvalidEmailsLoadedAction(invalidEmails)));
  }

  loadStudentsByClass(classId: number) {
    this._sharedFacade.loadStudentsByClass(classId);
  }

  resetStudentSearch() {
    this._sharedFacade.resetStudentSearchResult();
  }

  loadPredefinedTextContents() {
    this._sharedFacade.loadPredefinedTextContents();
  }
}
